import { useLazyQuery } from "@apollo/client";
import {
  useGetLearnersByOrgIdLazyQuery,
  useGetMenteesByMentorLazyQuery,
  useGetUsersRoleByOrgLazyQuery,
} from "graphql/__generated__/hooks";
import { GET_STUDENTS_BY_HOME_ORG } from "graphql/user/user.query";
import { useAuthUser } from "hooks/useAuthUser";
import { useAppSelector } from "hooks/useAppSelector";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { ROLES, User } from "types/user";
import { organizationSelectedSelector } from "reduxStore/organization/selectors";

export const useCustomUserList = (role: ROLES) => {
  const { organizationId } = useParams();
  const { user, role: userRole } = useAuthUser();
  const organization = useAppSelector(organizationSelectedSelector);
  const [
    getLearnersByOrgId,
    { data: learnersData, loading: learnersLoading, error: learnersError },
  ] = useGetLearnersByOrgIdLazyQuery();
  const [
    getOrgStudents,
    {
      data: orgStudentsData,
      loading: orgStudentsLoading,
      error: orgStudentsError,
    },
  ] = useGetUsersRoleByOrgLazyQuery();
  const [
    getMenteesByMentor,
    { data: menteesData, loading: menteesLoading, error: menteesError },
  ] = useGetMenteesByMentorLazyQuery();
  const [
    getHomeOrgStudents,
    {
      data: homeOrgStudentsData,
      loading: homeOrgStudentsLoading,
      error: homeOrgStudentsError,
    },
  ] = useLazyQuery(GET_STUDENTS_BY_HOME_ORG);

  const coachOrgId = organization?.org_id || user?.org_id || undefined;

  const users = useMemo(() => {
    if (role !== ROLES.STUDENT) {
      return [];
    }

    const byId = new Map<string, User>();

    for (const graduationClass of learnersData?.graduation_class ?? []) {
      for (const row of graduationClass.students ?? []) {
        const student = row.student;
        if (!student?.user_id) continue;
        byId.set(student.user_id, student as User);
      }
    }

    for (const membership of orgStudentsData?.organization_user ?? []) {
      const student = membership.user;
      if (!student?.user_id) continue;
      byId.set(student.user_id, {
        ...(byId.get(student.user_id) || {}),
        ...student,
      } as User);
    }

    for (const student of homeOrgStudentsData?.user ?? []) {
      if (!student?.user_id) continue;
      byId.set(student.user_id, {
        ...(byId.get(student.user_id) || {}),
        ...student,
      } as User);
    }

    for (const row of menteesData?.mentoring ?? []) {
      const student = row.mentee;
      if (!student?.user_id) continue;
      byId.set(student.user_id, {
        ...(byId.get(student.user_id) || {}),
        ...student,
      } as User);
    }

    return [...byId.values()];
  }, [learnersData, orgStudentsData, homeOrgStudentsData, menteesData, role]);

  const loading = useMemo(() => {
    return learnersLoading || orgStudentsLoading || homeOrgStudentsLoading || menteesLoading;
  }, [learnersLoading, orgStudentsLoading, homeOrgStudentsLoading, menteesLoading]);

  const error = useMemo(() => {
    return learnersError || orgStudentsError || homeOrgStudentsError || menteesError;
  }, [learnersError, orgStudentsError, homeOrgStudentsError, menteesError]);

  useEffect(() => {
    if (
      role === ROLES.STUDENT &&
      organizationId &&
      (userRole === ROLES.ADMIN ||
        userRole === ROLES.MANAGER ||
        userRole === ROLES.DIRECTOR)
    ) {
      getLearnersByOrgId({ variables: { orgId: organizationId } });
      getOrgStudents({
        variables: { orgId: organizationId, role: ROLES.STUDENT },
      });
      getHomeOrgStudents({ variables: { orgId: organizationId } });
    } else if (role === ROLES.STUDENT && userRole === ROLES.COACH) {
      // Always include assigned mentees (mentoring relationship).
      if (user?.user_id) {
        getMenteesByMentor({ variables: { mentor: user.user_id } });
      }
      // Also list org learners when we know the coach's org.
      if (!organizationId && coachOrgId) {
        getLearnersByOrgId({ variables: { orgId: coachOrgId } });
        getOrgStudents({
          variables: { orgId: coachOrgId, role: ROLES.STUDENT },
        });
        getHomeOrgStudents({ variables: { orgId: coachOrgId } });
      }
    }
  }, [
    organizationId,
    userRole,
    role,
    coachOrgId,
    user?.user_id,
    getLearnersByOrgId,
    getOrgStudents,
    getHomeOrgStudents,
    getMenteesByMentor,
  ]);

  return { users, loading, error, userRole };
};
